// „Преглед към" момент от времевата линия — чиста логика без React
// (тестове: momentFocus.test.ts): кои резервации са на масите в избрания
// момент (за подчертаването в дясната колона), маркерите на линията и
// натовареността на деня (малката графика над пистата).

import type { Reservation } from "../../../api/reservations";
import type { ListTab } from "./reservationList";
import { listTabOf } from "./reservationList";
import {
  MINUTE_MS,
  formatClock,
  formatClockOfDay,
  minuteOfDay,
  momentAtMinute,
  toDateKey,
  type TimelineRange,
} from "./dayTime";
import {
  ARRIVING_SOON_MINUTES,
  occupiesAt,
  reservationEndMs,
  reservationStartMs,
} from "./tableStatus";

/**
 * Ролята на резервацията спрямо избрания момент:
 * - active — държи масата си в момента (резервирана или заета) — точно
 *   както я вижда платното (`tableStateAt`);
 * - soon — предстояща, която пристига до `ARRIVING_SOON_MINUTES` след момента;
 * - idle — всичко останало (в прегледа се приглушава).
 */
type MomentRole = "active" | "soon" | "idle";

/**
 * Резервацията е на масите си в момента — същите правила като платното:
 * предстояща с [начало, край) ∋ момента или настанена/приключила, чийто
 * интервал на заетост го покрива (приключилата — само в минал момент).
 * Резервация без маса на схемата никога не е активна (предстоящата пак
 * може да е „скоро" — трябва ѝ маса).
 */
export const isActiveAt = (reservation: Reservation, momentMs: number, nowMs: number): boolean => {
  // Масата ѝ е изтрита от схемата („Без маса") — платното няма какво да
  // покаже, затова и списъкът не я брои за „на масите".
  if (reservation.tableNames.length === 0) return false;
  if (reservation.status === "upcoming") {
    return reservationStartMs(reservation) <= momentMs && momentMs < reservationEndMs(reservation);
  }
  return occupiesAt(reservation, momentMs, nowMs) !== null;
};

export const momentRoleOf = (reservation: Reservation, momentMs: number, nowMs: number): MomentRole => {
  if (isActiveAt(reservation, momentMs, nowMs)) return "active";
  if (reservation.status === "upcoming") {
    const startMs = reservationStartMs(reservation);
    if (startMs > momentMs && startMs <= momentMs + ARRIVING_SOON_MINUTES * MINUTE_MS) return "soon";
  }
  return "idle";
};

export interface MomentFocus {
  momentMs: number;
  // Часът за надписите — краят на деня е „24:00", не „00:00".
  clock: string;
  roles: ReadonlyMap<number, MomentRole>;
  // Колко активни има във всеки таб — точка на таба, когато не е отворен.
  activeByTab: Record<ListTab, number>;
}

/** Ролите на всички резервации на деня за момента (отказаните — idle). */
export const momentFocusOf = (
  reservations: Reservation[],
  momentMs: number,
  nowMs: number,
  // Денят на прегледа — за „24:00" в края му.
  dateKey?: string,
): MomentFocus => {
  const roles = new Map<number, MomentRole>();
  const activeByTab: Record<ListTab, number> = { upcoming: 0, seated: 0, past: 0 };
  for (const reservation of reservations) {
    const role = reservation.status === "cancelled" ? "idle" : momentRoleOf(reservation, momentMs, nowMs);
    roles.set(reservation.id, role);
    if (role === "active") activeByTab[listTabOf(reservation)] += 1;
  }
  const clock = dateKey ? formatClockOfDay(momentMs, dateKey) : formatClock(momentMs);
  return { momentMs, clock, roles, activeByTab };
};

/** Видът на маркера на линията по статуса на резервацията. */
export type MarkerTone = "upcoming" | "seated" | "done" | "missed";

export interface TimelineMarker {
  minute: number;
  tone: MarkerTone;
  // Колко резервации започват в същата минута (една точка за всички).
  count: number;
}

const MARKER_TONE: Partial<Record<Reservation["status"], MarkerTone>> = {
  upcoming: "upcoming",
  seated: "seated",
  completed: "done",
  expired: "missed",
};

// Когато в една минута започват няколко — точката е с най-важния вид.
const TONE_PRIORITY: MarkerTone[] = ["upcoming", "seated", "done", "missed"];

/**
 * Маркерите на линията: по една точка на минутата, в която започва
 * резервация на деня `dateKey` (без отказаните), подредени по час.
 */
export const timelineMarkersOf = (reservations: Reservation[], dateKey: string): TimelineMarker[] => {
  const byMinute = new Map<number, TimelineMarker>();
  for (const reservation of reservations) {
    const tone = MARKER_TONE[reservation.status];
    if (!tone) continue;
    const startMs = reservationStartMs(reservation);
    if (toDateKey(new Date(startMs)) !== dateKey) continue;
    const minute = minuteOfDay(startMs);
    const existing = byMinute.get(minute);
    if (!existing) {
      byMinute.set(minute, { minute, tone, count: 1 });
      continue;
    }
    existing.count += 1;
    if (TONE_PRIORITY.indexOf(tone) < TONE_PRIORITY.indexOf(existing.tone)) existing.tone = tone;
  }
  return [...byMinute.values()].sort((left, right) => left.minute - right.minute);
};

// Стъпката на графиката „натовареност" над линията.
export const LOAD_SLOT_MINUTES = 15;

/**
 * Интервалът, в който резервацията натоварва заведението, за графиката:
 * предстоящата/неизпълнената — планираният ѝ час; настанената — от
 * настаняването до по-късното от планирания край и „сега" (не до края на
 * деня — графиката е очакване, не заетост); приключилата — до
 * освобождаването.
 */
const loadInterval = (reservation: Reservation, nowMs: number): { startMs: number; endMs: number } | null => {
  if (reservation.status === "cancelled") return null;
  const plannedStart = reservationStartMs(reservation);
  const plannedEnd = reservationEndMs(reservation);
  if (!reservation.seatedAt || reservation.status === "upcoming" || reservation.status === "expired") {
    return { startMs: plannedStart, endMs: plannedEnd };
  }
  const seatedMs = Date.parse(reservation.seatedAt);
  if (reservation.status === "seated") return { startMs: seatedMs, endMs: Math.max(plannedEnd, nowMs) };
  const releasedMs = reservation.releasedAt ? Date.parse(reservation.releasedAt) : nowMs;
  return { startMs: seatedMs, endMs: Math.max(releasedMs, seatedMs + MINUTE_MS) };
};

/**
 * Натовареността на деня: гостите на масите във всеки слот от
 * `LOAD_SLOT_MINUTES` в обхвата на линията (по средата на слота).
 */
export const timelineLoadOf = (
  reservations: Reservation[],
  dateKey: string,
  range: TimelineRange,
  nowMs: number,
): number[] => {
  const intervals = reservations
    .map((reservation) => ({ guests: reservation.guests, interval: loadInterval(reservation, nowMs) }))
    .filter((item): item is { guests: number; interval: { startMs: number; endMs: number } } => item.interval !== null);
  const slots: number[] = [];
  for (let minute = range.startMinute; minute < range.endMinute; minute += LOAD_SLOT_MINUTES) {
    const middleMs = momentAtMinute(dateKey, minute + LOAD_SLOT_MINUTES / 2);
    let guests = 0;
    for (const { guests: count, interval } of intervals) {
      if (interval.startMs <= middleMs && middleMs < interval.endMs) guests += count;
    }
    slots.push(guests);
  }
  return slots;
};
